import React, { Fragment, useContext, useState } from "react";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { Button, Dropdown, DropdownButton, OverlayTrigger, Tooltip } from "react-bootstrap";
import { BasicContext } from "../../App";
import { BackOfficeContext } from "./../../pages/admin/BackOffice";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBan, faCheckToSlot, faEdit, faEllipsisV, faSpinner, faUserCheck } from "@fortawesome/free-solid-svg-icons";
import UpdateClassDataModal from "./../../components/modals/UpdateClassDataModal";
import AddUserToClassModal from "../../pages/admin/classes/AddUserToClass";
import ConfirmModal from "../modals/ConfirmModal";
import { postMethod } from "../../requests";
import { faTrashAlt } from "@fortawesome/free-regular-svg-icons";
import DeleteClassModal from "../modals/DeleteClassModal";

const ScheduleAdminButtons = (props) => {
    const { c, j, getSchedule, filterDates, setLoading, reloadData, permissions } = props;
    const { t } = useTranslation();
    const { changeView, checkServerData, setAlerts } = useContext(BasicContext);
    const { settings } = useContext(BackOfficeContext);
    const [showUpdateClassData, setShowUpdateClassData] = useState(false);
    const [showAddUserToClassModal, setShowAddUserToClassModal] = useState(false);
    const [showCancelClassModal, setShowCancelClassModal] = useState(false);
    const [showDeleteClassModal, setShowDeleteClassModal] = useState(false);

    const cancelClass = async (c) => {
        const res = await postMethod("/cancel-class", { classID: c.class_id });
        if (checkServerData(res)) {
            setAlerts([{ variant: "success", message: t(`success.${res.data.code}`), fading: 1500, id: "classCancelled" }]);
            reloadData();
        }
    };

    const deleteClass = async (deleteAllClasses) => {
        const res = await postMethod("/delete-class", { classID: c.class_id, className: c.class_name, classStartTime: c.class_start_time, deleteAllClasses: deleteAllClasses });
        if (checkServerData(res)) {
            setAlerts([{ variant: "success", message: t(`success.${res.data.code}`), fading: 1500, id: "classDeleted" }]);
            reloadData();
        }
    };

    return (
        <Fragment>
            {!permissions ? (
                <FontAwesomeIcon icon={faSpinner} className="ms-2 rotate-center" />
            ) : (
                <Fragment>
                    {permissions.checkAttendance && c.canUserCheckAttendence && c.class_status_code === "AVAILABLE" && (
                        <>
                            {permissions.checkAttendanceHistory ||
                            moment().format() <=
                                moment(c.class_end_time)
                                    .add(settings.filter((e) => e.setting_code === "ATTENDANCE_CHECK_WINDOW")[0].setting_value_int, "h")
                                    .format() ? (
                                <OverlayTrigger placement="bottom" delay={{ show: 150, hide: 100 }} overlay={<Tooltip id={`checkAttendanceTooltip${j}`}>{t("schedule.checkAttendance")}</Tooltip>}>
                                    <Button
                                        variant="outline-primary"
                                        className="me-2"
                                        size="sm"
                                        onClick={() => changeView({ nav: "schedule", sub: "checkAttendance", params: { class: c, changedData: false } })}
                                    >
                                        <FontAwesomeIcon icon={faUserCheck} />
                                    </Button>
                                </OverlayTrigger>
                            ) : (
                                <OverlayTrigger overlay={<Tooltip id="tooltip-disabled">{t("classes.classesEnded")}</Tooltip>}>
                                    <span className="d-inline-block">
                                        <Button disabled style={{ pointerEvents: "none" }} variant="outline-primary" className="me-2" size="sm">
                                            <FontAwesomeIcon icon={faUserCheck} />
                                        </Button>
                                    </span>
                                </OverlayTrigger>
                            )}
                        </>
                    )}
                    {((permissions.cancelClass && c.class_status_code === "AVAILABLE" && new Date() < new Date(c.class_start_time)) ||
                        (permissions.addToClass && c.class_status_code === "AVAILABLE" && new Date() < new Date(c.class_signup_end_time)) ||
                        permissions.deleteClass ||
                        permissions.editClass) && (
                        <DropdownButton id="dropdownMoreOptions" variant="outline-secondary" className="d-inline-block" size="sm" title={<FontAwesomeIcon icon={faEllipsisV} />}>
                            {permissions.cancelClass && c.class_status_code === "AVAILABLE" && new Date() < new Date(c.class_start_time) && (
                                <Dropdown.Item as={Button} variant="link" disabled={moment(c.class_end_time).format() < moment().format()} onClick={() => setShowCancelClassModal(c)}>
                                    <FontAwesomeIcon icon={faBan} className="me-2" />
                                    {t("schedule.cancelClass")}
                                </Dropdown.Item>
                            )}

                            {permissions.addToClass && c.class_status_code === "AVAILABLE" && new Date() < new Date(c.class_signup_end_time) && (
                                <Dropdown.Item
                                    as={Button}
                                    variant="link"
                                    disabled={moment(c.class_end_time).format() < moment().format()}
                                    onClick={() => changeView({ nav: "reservations", sub: "createReservation", params: { class: c } })}
                                >
                                    <FontAwesomeIcon icon={faCheckToSlot} className="me-2" />
                                    {t("reservations.makeReservation")}
                                </Dropdown.Item>
                            )}
                            {permissions.editClass && (
                                <Dropdown.Item as={Button} variant="link" onClick={() => setShowUpdateClassData({ c: c })}>
                                    <FontAwesomeIcon icon={faEdit} className="me-2" />
                                    {t("schedule.editClass")}
                                </Dropdown.Item>
                            )}
                            {permissions.deleteClass && (
                                <Dropdown.Item as={Button} variant="link" onClick={() => setShowDeleteClassModal({ c: c })}>
                                    <FontAwesomeIcon icon={faTrashAlt} className="me-2" />
                                    {t("schedule.deleteClass")}
                                </Dropdown.Item>
                            )}
                        </DropdownButton>
                    )}
                </Fragment>
            )}
            {showUpdateClassData && (
                <UpdateClassDataModal
                    onClose={() => {
                        setShowUpdateClassData(false);
                        setAlerts([]);
                    }}
                    c={showUpdateClassData.c}
                    reloadData={() => {
                        setLoading(true);
                        setShowUpdateClassData(false);
                        setTimeout(() => {
                            getSchedule(filterDates.startDate, filterDates.endDate);
                        }, 150);
                    }}
                />
            )}
            {showAddUserToClassModal && (
                <AddUserToClassModal
                    c={showAddUserToClassModal}
                    onClose={() => {
                        setShowAddUserToClassModal(false);
                        setAlerts([]);
                    }}
                    reloadData={() => {
                        setLoading(true);
                        setShowAddUserToClassModal(false);
                        setTimeout(() => {
                            getSchedule(filterDates.startDate, filterDates.endDate);
                        }, 150);
                    }}
                />
            )}
            {showCancelClassModal && (
                <ConfirmModal
                    show={true}
                    onClose={() => {
                        setShowCancelClassModal(false);
                        setAlerts([]);
                    }}
                    onProceed={() => cancelClass(showCancelClassModal)}
                    title={t("cancelClassModal.title")}
                    text={t("cancelClassModal.text")}
                    proceedTxt={t("cancelClassModal.proceedTxt")}
                    cancelTxt={t("cancelClassModal.cancelTxt")}
                />
            )}
            {showDeleteClassModal && (
                <DeleteClassModal
                    show={true}
                    onClose={() => {
                        setShowDeleteClassModal(false);
                        setAlerts([]);
                    }}
                    onProceed={(deleteAllClasses) => deleteClass(deleteAllClasses)}
                    title={t("deleteClassModal.title")}
                    text={t("deleteClassModal.text")}
                    proceedTxt={t("deleteClassModal.proceedTxt")}
                    cancelTxt={t("deleteClassModal.cancelTxt")}
                />
            )}
        </Fragment>
    );
};

export default ScheduleAdminButtons;
